<template>
  <div
    ref="root"
    class="wave-mask"
    :class="{ seekable }"
    :style="{ height: displayHeight }"
    @click="onClick"
  >
    <div class="wave-layers" :class="{ barred: bars }">
      <div class="wave-layer wave-base" :style="maskStyle"></div>
      <div
        v-if="showLoaded"
        class="wave-layer wave-loaded"
        :style="[maskStyle, loadedStyle]"
      ></div>
      <div
        v-if="progress > 0"
        class="wave-layer wave-played"
        :style="[maskStyle, playedStyle]"
      ></div>
    </div>
    <div
      v-if="showCursor"
      class="wave-cursor"
      :style="{ left: progress + '%' }"
    ></div>
  </div>
</template>

<script>
// The one waveform visual, shared by every surface (session library player,
// audio-page tiles, session-card thumbnails). The server's pre-rendered
// monochrome bar SVG (/download/spectrogram?type=waveform) is applied as a
// CSS alpha mask so it paints instantly with no audio download, and all
// colouring lives here in CSS — a muted base layer plus an accent "played"
// layer clipped to progress. Consumers differ only in size and whether the
// cursor/seek interactions are enabled.
import { getWaveformSvgUrl } from "@/filters/utils";

export default {
  name: "WaveMask",
  props: {
    hash: { type: String, default: "" },
    // A full waveform URL instead of a sound's (Discover's whole-session
    // /download/spectrogram/session/<name>?type=waveform). Never
    // normalised: the amplitude stays true.
    src: { type: String, default: null },
    // Server-side render resolution; CSS scales to the container.
    width: { type: Number, default: 600 },
    height: { type: Number, default: 64 },
    // Played fraction, 0-100.
    progress: { type: Number, default: 0 },
    // Downloaded/buffered fraction, 0-100. With a fixed-bitrate stream,
    // buffered time over the known duration IS the download progress, so
    // this renders the SoundCloud-style loaded shade without needing a
    // Content-Length on the chunked response.
    loaded: { type: Number, default: 0 },
    // Cut the silhouette into vertical bars, as the boards and the app's
    // WaveformStrip draw it. Width and pitch come from the consumer's CSS:
    // --wave-bar and --wave-pitch (2px on a 4px pitch by default; the touch
    // spec). The cursor stays unbroken.
    bars: { type: Boolean, default: false },
    showCursor: { type: Boolean, default: false },
    // Emit 'seek' with a 0-1 fraction on click.
    seekable: { type: Boolean, default: false },
  },
  emits: ["seek"],
  computed: {
    // The fetched-range shade shows only while the sound is still
    // downloading, as the app's WaveformStrip does. Once it has loaded the
    // unplayed part goes back to the base alpha instead of staying lit.
    showLoaded() {
      return this.loaded > 0 && this.loaded < 99.5;
    },
    displayHeight() {
      return `${this.height}px`;
    },
    maskStyle() {
      const href = this.src || (this.hash ? getWaveformSvgUrl(this.hash, this.width, this.height) : "");
      if (!href) return {};
      const url = `url("${href}")`;
      return {
        "-webkit-mask-image": url,
        "mask-image": url,
        "-webkit-mask-size": "100% 100%",
        "mask-size": "100% 100%",
        "-webkit-mask-repeat": "no-repeat",
        "mask-repeat": "no-repeat",
        // The SVG bars are opaque on a transparent ground; mask by alpha so
        // they cut at full strength regardless of the browser's default
        // match-source interpretation.
        "-webkit-mask-mode": "alpha",
        "mask-mode": "alpha",
      };
    },
    playedStyle() {
      return { clipPath: `inset(0 ${100 - this.progress}% 0 0)` };
    },
    loadedStyle() {
      return { clipPath: `inset(0 ${100 - this.loaded}% 0 0)` };
    },
  },
  methods: {
    onClick(e) {
      if (!this.seekable) return;
      const rect = this.$refs.root.getBoundingClientRect();
      const frac = Math.min(
        Math.max((e.clientX - rect.left) / rect.width, 0),
        1,
      );
      this.$emit("seek", frac);
    },
  },
};
</script>

<style scoped>
.wave-mask {
  position: relative;
  width: 100%;
}

.wave-mask.seekable {
  cursor: pointer;
}

.wave-layers {
  position: absolute;
  inset: 0;
}
.wave-layers.barred {
  -webkit-mask-image: repeating-linear-gradient(
    90deg,
    #000 0 var(--wave-bar, 2px),
    transparent var(--wave-bar, 2px) var(--wave-pitch, 4px)
  );
  mask-image: repeating-linear-gradient(
    90deg,
    #000 0 var(--wave-bar, 2px),
    transparent var(--wave-bar, 2px) var(--wave-pitch, 4px)
  );
}
.wave-layer {
  position: absolute;
  inset: 0;
}

.wave-base {
  background: rgba(255, 255, 255, 0.25);
}

.wave-loaded {
  background: rgba(255, 255, 255, 0.45);
}

.wave-played {
  background: var(--cl-accent);
}

/* No transition: the players drive progress from requestAnimationFrame at
   frame rate, so easing here would only add lag. */
.wave-cursor {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #ffffff;
  pointer-events: none;
}
</style>
